import { InMemoryCache, IntrospectionFragmentMatcher } from 'apollo-cache-inmemory';
import ApolloClient from 'apollo-client';
import { ApolloLink } from 'apollo-link';
import { setContext } from 'apollo-link-context';
import { onError } from 'apollo-link-error';
import { createHttpLink } from 'apollo-link-http';
import { createPersistedQueryLink } from 'apollo-link-persisted-queries';
import { sha256 } from 'crypto-hash/browser';
import { UNAUTHORIZED, FORBIDDEN } from 'http-status';
import VueApollo from 'vue-apollo';
import api from '../api';
import buildinfo from '../buildnum.json';
import store from '../store'; // eslint-disable-line import/no-cycle
import { Headers } from '../utils/auth-session.util';
import loggingUtil from '../utils/logging-util';
import introspectionQueryResultData from './types/fragmentTypes.json';

const appBuild = buildinfo.buildnum || 'dev';
const {
  VUE_APP_APP_NAME: appName,
  VUE_APP_APP_VERSION: appVersion,
  VUE_APP_GRAPH_URL: appGraphUrl
} = process.env;

const getHeaders = () => {
  const {
    application,
    student: { sessionId }
  } = store.get();

  const omitSession =
    (application && application.kwtMode === 'tester') ||
    ['preview', 'previewLite'].includes(application.kwtMode);

  const headers = {
    [Headers.APP]: appName,
    [Headers.APP_VERSION]: appVersion,
    [Headers.APP_BUILD]: appBuild,
    ...(!omitSession ? { [Headers.APP_SESSION]: sessionId } : {})
  };

  return headers;
};

const getClientLinks = () => {
  const authLink = setContext(() => {
    const headers = getHeaders();
    return { headers: { ...headers, [Headers.APP_LOCALE]: 'en-US' } };
  });

  /* eslint-disable consistent-return */
  const errorLink = onError(({ graphQLErrors = [], networkError }) => {
    const headers = getHeaders();

    const { response: { status, statusCode } = {} } = networkError || {};

    if (status === UNAUTHORIZED || status === FORBIDDEN) {
      // I am temporarily returning out of the errorLink so the app can continue to run.
      // TODO: Enforce this commented out code when all KWT back-end envs have implemented the graph api
      // return { status }
      return;
    }

    // don't try to log if no internet
    if (statusCode && !graphQLErrors.length) {
      return;
    }

    // log non-network-failure, non-auth related errors
    const errorEvent = loggingUtil.formatGraphErrors(graphQLErrors, networkError);
    api.createLogEvent('api-error', errorEvent, { headers });
  });

  const persistedQueriesLink = createPersistedQueryLink({ sha256 });

  const uploadLink = createHttpLink({
    uri: appGraphUrl
  });

  return { authLink, errorLink, persistedQueriesLink, uploadLink };
};

const setupClient = () => {
  const { authLink, errorLink, persistedQueriesLink, uploadLink } = getClientLinks();
  const fragmentMatcher = new IntrospectionFragmentMatcher({
    introspectionQueryResultData
  });

  const client = new ApolloClient({
    cache: new InMemoryCache({ fragmentMatcher }),
    connectToDevTools: process.env.NODE_ENV !== 'production',
    link: ApolloLink.from([authLink, errorLink, persistedQueriesLink, uploadLink]),
    resolvers: {}
  });

  return client;
};

const apolloClient = setupClient();

const setupProvider = () =>
  new VueApollo({
    defaultClient: apolloClient
  });

export default {
  apolloClient,
  setupProvider
};
